import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import markUrl from '../../assets/brand/wonder-forest-mark.png';
import markSunUrl from '../../assets/brand/wonder-forest-mark-sun.png';
import { cn } from '../../lib/utils';

export const BRAND_NAME = 'Wonder Forest';

const logoVariants = cva('inline-flex items-center text-foreground', {
  variants: {
    variant: {
      mark: '',
      lockup: 'flex-row gap-2.5',
      stacked: 'flex-col gap-3',
    },
    size: {
      sm: 'text-sm',
      md: 'text-base',
      lg: 'text-xl',
      xl: 'text-2xl',
    },
  },
  defaultVariants: {
    variant: 'mark',
    size: 'md',
  },
});

const logoMarkVariants = cva('shrink-0 w-auto object-contain select-none', {
  variants: {
    size: {
      sm: 'h-5',
      md: 'h-7',
      lg: 'h-12',
      xl: 'h-24',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

export interface LogoMarkProps extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  /** Include the orange sun accent (large lockups). Off by default so the navbar mark stays quiet. */
  accent?: boolean;
}

/**
 * The bare brand mark — the transparent PNG in `src/assets/brand/` (the same artwork the Wonder
 * Forest design system uses in its header). Pass `alt` (or `alt=""` when a visible name sits next
 * to it).
 */
const LogoMark = React.forwardRef<HTMLImageElement, LogoMarkProps>(
  ({ accent = false, className, alt = BRAND_NAME, ...props }, ref) => (
    <img
      ref={ref}
      src={accent ? markSunUrl : markUrl}
      alt={alt}
      draggable={false}
      data-brand="wonder-forest-mark"
      data-brand-accent={accent ? '' : undefined}
      className={className}
      {...props}
    />
  ),
);
LogoMark.displayName = 'LogoMark';

export interface LogoProps
  extends React.HTMLAttributes<HTMLSpanElement>, VariantProps<typeof logoVariants> {
  /**
   * Accessible name of the mark. Used as the image `alt` for the `mark` variant and as the visible
   * text fallback when no `children` are given. Defaults to the brand name.
   */
  label?: string;
  /** Show the orange sun accent (large lockups). */
  accent?: boolean;
  /** Visible app name for `lockup` / `stacked` (e.g. `<FormattedMessage id="nav.appName" />`). */
  children?: React.ReactNode;
}

/**
 * Wonder Forest brand mark.
 *
 * - `mark` — icon only, exposed as an image named by `label`.
 * - `lockup` — mark + name on one line (navbar).
 * - `stacked` — large mark with the name underneath (login).
 *
 * When a visible name is rendered the image gets an empty `alt` so the name is not read twice.
 */
const Logo = React.forwardRef<HTMLSpanElement, LogoProps>(
  (
    { className, variant = 'mark', size, label = BRAND_NAME, accent = false, children, ...props },
    ref,
  ) => {
    const showName = variant !== 'mark';
    const name = children ?? label;

    return (
      <span ref={ref} className={cn(logoVariants({ variant, size }), className)} {...props}>
        <LogoMark
          accent={accent}
          alt={showName ? '' : label}
          className={logoMarkVariants({ size })}
        />
        {showName && <span className="font-semibold tracking-tight whitespace-nowrap">{name}</span>}
      </span>
    );
  },
);
Logo.displayName = 'Logo';

export { Logo, LogoMark, logoVariants };
